import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  type ReactNode,
} from "react";
import type { TourState, TourAction, TourId, TourStep } from "./types";

const initialState: TourState = {
  tourId: null,
  steps: [],
  stepIndex: 0,
  run: false,
};

function tourReducer(state: TourState, action: TourAction): TourState {
  switch (action.type) {
    case "START_TOUR":
      return {
        tourId: action.tourId,
        steps: action.steps,
        stepIndex: 0,
        run: true,
      };
    case "STOP_TOUR":
      return { ...state, run: false };
    case "SET_STEP_INDEX":
      return { ...state, stepIndex: action.index };
    case "SET_RUN":
      return { ...state, run: action.run };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

interface TourContextValue {
  tourState: TourState;
  startTour: (tourId: TourId, steps: TourStep[]) => void;
  stopTour: () => void;
  resetTour: () => void;
  setStepIndex: (index: number) => void;
  setRun: (run: boolean) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [tourState, dispatch] = useReducer(tourReducer, initialState);

  const startTour = useCallback((tourId: TourId, steps: TourStep[]) => {
    dispatch({ type: "START_TOUR", tourId, steps });
  }, []);

  const stopTour = useCallback(() => {
    dispatch({ type: "STOP_TOUR" });
  }, []);

  const resetTour = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const setStepIndex = useCallback((index: number) => {
    dispatch({ type: "SET_STEP_INDEX", index });
  }, []);

  const setRun = useCallback((run: boolean) => {
    dispatch({ type: "SET_RUN", run });
  }, []);

  return (
    <TourContext
      value={{
        tourState,
        startTour,
        stopTour,
        resetTour,
        setStepIndex,
        setRun,
      }}
    >
      {children}
    </TourContext>
  );
}

export function useTourStore() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTourStore must be used within TourProvider");
  return ctx;
}
